import React, { useState } from 'react';
import { Briefcase, Calendar, CalendarClock, Users, X } from 'lucide-react';
import JobBoardTab from './JobBoardTab';
import EventCalendarTab from './EventCalendarTab';
import EventPlannerTab from './EventPlannerTab';

const GigBoardTab = ({ onViewInMyEvents, onDiscordShare }) => {
  const [activeView, setActiveView] = useState('landing'); // landing, job-board, event-calendar, marketplace
  const [activeModal, setActiveModal] = useState(null);
  const [autoOpenCalendar, setAutoOpenCalendar] = useState(false);
  
  // Debug: Log received props
  console.log('🎯 GigBoardTab props:', { onViewInMyEvents: !!onViewInMyEvents, onDiscordShare: !!onDiscordShare });
  
  // Listen for event to open calendar
  React.useEffect(() => {
    const handleOpenCalendar = (event) => {
      console.log('📋 GigBoardTab received gigBoardOpenCalendar event');
      setActiveView('event-calendar');
      setAutoOpenCalendar(true);
      // Re-dispatch the event after a short delay so EventCalendarTab can mount
      const { eventId, eventDate } = event.detail || {};
      setTimeout(() => {
        console.log('📋 GigBoardTab re-dispatching djBoothOpenEventCalendar for EventCalendarTab');
        window.dispatchEvent(new CustomEvent('djBoothOpenEventCalendar', {
          detail: { eventId, eventDate }
        }));
      }, 50);
    };

    window.addEventListener('gigBoardOpenCalendar', handleOpenCalendar);
    return () => window.removeEventListener('gigBoardOpenCalendar', handleOpenCalendar);
  }, []);

  // Listen for event to open the Event Planner (invite links)
  React.useEffect(() => {
    const handleOpenPlanner = (event) => {
      setActiveView('event-planner');
      const { eventId } = event.detail || {};
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('plannerOpenEvent', { detail: { eventId } }));
      }, 50);
    };
    window.addEventListener('gigBoardOpenPlanner', handleOpenPlanner);
    return () => window.removeEventListener('gigBoardOpenPlanner', handleOpenPlanner);
  }, []);
  
  // If Job Board is active, show that component
  if (activeView === 'job-board') {
    return <JobBoardTab onBack={() => setActiveView('landing')} />;
  }

  // If Event Calendar is active, show that component
  if (activeView === 'event-calendar') {
    return <EventCalendarTab 
      onBack={() => setActiveView('landing')} 
      autoOpenCalendar={autoOpenCalendar}
      onCalendarOpened={() => setAutoOpenCalendar(false)}
      onViewInMyEvents={onViewInMyEvents}
      onDiscordShare={onDiscordShare}
    />;
  }

  // If Event Planner is active, show that component
  if (activeView === 'event-planner') {
    return <EventPlannerTab onBack={() => setActiveView('landing')} />;
  }

  const cards = [
    {
      id: 'job-board',
      title: 'Job Board',
      icon: Briefcase,
      description: 'Post gig opportunities & browse available gigs',
      color: '#00ffff',
      features: [
        'Post gig opportunities (venue owners/promoters)',
        'Browse available gigs (DJs)',
        'Filter by location, date, pay range, genre',
        'Apply to gigs or contact posters'
      ]
    },
    {
      id: 'event-calendar',
      title: 'Event Calendar',
      icon: Calendar,
      description: 'DJ event calendar & upcoming community gigs',
      color: '#ff00ff',
      features: [
        'DJ event calendar/schedule',
        'Upcoming gigs from community members',
        'RSVP or mark interest',
        'Add your events to the calendar'
      ]
    },
    {
      id: 'event-planner',
      title: 'Event Planner',
      icon: CalendarClock,
      description: 'Build a DJ lineup — post an event & let DJs claim their slots',
      color: '#f59e0b',
      features: [
        'Plan stream, club, bar, lounge or private events',
        'Auto-generated timetable with claimable DJ slots',
        'Open or request-to-approve claiming',
        'Auto-posts to the Event Calendar'
      ]
    }
  ];

  const renderModal = () => {
    if (!activeModal) return null;

    const card = cards.find(c => c.id === activeModal);
    const IconComponent = card.icon;

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '1rem'
        }}
        onClick={() => setActiveModal(null)}
      >
        <div
          style={{
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            background: 'rgba(26, 26, 46, 0.95)',
            borderRadius: '16px',
            padding: '2rem',
            overflow: 'auto',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={() => setActiveModal(null)}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'rgba(255, 0, 128, 0.2)',
              border: '1px solid rgba(255, 0, 128, 0.4)',
              color: '#ff0080',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.25rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 128, 0.4)';
              e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 128, 0.2)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <X size={18} />
          </button>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${card.color}, ${card.color}40)`,
              marginBottom: '1rem'
            }}>
              <IconComponent size={40} style={{ color: card.color }} />
            </div>
            <h2 style={{
              fontSize: '2rem',
              fontWeight: '700',
              background: `linear-gradient(135deg, ${card.color}, #fff)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: '0 0 0.5rem 0'
            }}>
              {card.title}
            </h2>
            <p style={{
              fontSize: '1rem',
              color: 'rgba(255, 255, 255, 0.7)',
              margin: 0
            }}>
              {card.description}
            </p>
          </div>

          {/* Coming Soon Message */}
          <div style={{
            background: 'rgba(0, 255, 255, 0.1)',
            border: '1px solid rgba(0, 255, 255, 0.3)',
            borderRadius: '12px',
            padding: '2rem',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <p style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#00ffff',
              margin: '0 0 0.5rem 0'
            }}>
              🚀 Coming Soon!
            </p>
            <p style={{
              fontSize: '0.95rem',
              color: 'rgba(255, 255, 255, 0.7)',
              margin: 0
            }}>
              We're building this feature to help you connect with the DJ community. Stay tuned!
            </p>
          </div>

          {/* Planned Features */}
          <div>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#fff',
              marginBottom: '1rem'
            }}>
              Planned Features
            </h3>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              {card.features.map((feature, index) => (
                <li
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    marginBottom: '0.5rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <span style={{
                    fontSize: '1.25rem',
                    flexShrink: 0
                  }}>
                    ✨
                  </span>
                  <span style={{
                    fontSize: '0.95rem',
                    color: 'rgba(255, 255, 255, 0.8)',
                    lineHeight: '1.5'
                  }}>
                    {feature}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="gig-hub-tab" style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: 'calc(100vh - 220px)',
      maxHeight: '75vh',
      padding: '2.5rem 2rem',
      overflowY: 'auto'
    }}>
      {/* Cards Grid */}
      <div 
        className="gig-hub-cards-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem',
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%'
        }}>
        {cards.map((card) => {
          const IconComponent = card.icon;
          return (
            <div
              key={card.id}
              className="gig-hub-card"
              data-testid={`gig-hub-card-${card.id}`}
              onClick={() => {
                if (card.id === 'job-board') {
                  setActiveView('job-board');
                } else if (card.id === 'event-calendar') {
                  setActiveView('event-calendar');
                } else if (card.id === 'event-planner') {
                  setActiveView('event-planner');
                } else {
                  setActiveModal(card.id);
                }
              }}
              style={{
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '16px',
                padding: '2rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '360px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = card.color;
                e.currentTarget.style.boxShadow = `0 12px 36px ${card.color}40`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Icon */}
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${card.color}20, ${card.color}10)`,
                border: `2px solid ${card.color}40`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1.5rem'
              }}>
                <IconComponent size={40} style={{ color: card.color }} />
              </div>

              {/* Title */}
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#fff',
                margin: '0 0 0.75rem 0'
              }}>
                {card.title}
              </h3>

              {/* Description */}
              <p style={{
                fontSize: '0.95rem',
                color: 'rgba(255, 255, 255, 0.7)',
                margin: 0,
                lineHeight: '1.5'
              }}>
                {card.description}
              </p>

              {/* Click hint */}
              <div style={{
                marginTop: 'auto',
                padding: '0.5rem 1rem',
                background: `${card.color}20`,
                borderRadius: '8px',
                fontSize: '0.875rem',
                color: card.color,
                fontWeight: '600'
              }}>
                Click to explore →
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {renderModal()}
    </div>
  );
};

export default GigBoardTab;
